/**
 * Wizard de Onboarding para Suscripciones
 *
 * Se muestra cuando un usuario nuevo no tiene suscripción activa
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { getPlans } from '../services/api';
import PlanCardsGrid from './PlanCardsGrid';
import './OnboardingWizard.css';

function OnboardingWizard({ isOpen, onComplete }) {
  const { t } = useTranslation();
  const { createSubscription } = useSubscription();

  const [step, setStep] = useState(1); // 1: Welcome, 2: Plan Selection, 3: Confirming
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState(null);

  // Cargar planes disponibles al montar el componente
  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await getPlans();
      console.log('[OnboardingWizard] Planes cargados:', response);

      if (response.plans) {
        const sorted = [...response.plans].sort((a, b) => (a.order || 0) - (b.order || 0));
        setPlans(sorted);
        // Pre-select professional plan, fallback to first available
        const professional = sorted.find((p) => p.planId === 'professional');
        setSelectedPlan(professional ? professional.planId : sorted[0]?.planId || null);
      }
    } catch (err) {
      console.error('[OnboardingWizard] Error cargando planes:', err);
      setError(t('onboarding.errorLoadingPlans'));
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) {
      setError(t('onboarding.selectPlanError'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Crear suscripción con trial si es el plan profesional
      const startTrial = selectedPlan === 'professional';

      console.log('[OnboardingWizard] Creando suscripción:', { selectedPlan, startTrial });

      const result = await createSubscription(selectedPlan, startTrial);

      if (result.success) {
        console.log('[OnboardingWizard] Suscripción creada exitosamente');
        // Esperar un momento para mostrar éxito antes de cerrar
        setTimeout(() => {
          onComplete();
        }, 1500);
      } else {
        setError(result.error || t('onboarding.errorCreatingSubscription'));
      }
    } catch (err) {
      console.error('[OnboardingWizard] Error creando suscripción:', err);
      setError(err.message || t('onboarding.errorCreatingSubscription'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-container">
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="onboarding-step onboarding-welcome">
            <div className="welcome-icon">🎉</div>
            <h1>{t('onboarding.welcome')}</h1>
            <p className="welcome-subtitle">{t('onboarding.welcomeSubtitle')}</p>

            <div className="welcome-content">
              <div className="welcome-section">
                <span className="welcome-emoji">🍽️</span>
                <h3>{t('onboarding.feature1Title')}</h3>
                <p>{t('onboarding.feature1Description')}</p>
              </div>

              <div className="welcome-section">
                <span className="welcome-emoji">📊</span>
                <h3>{t('onboarding.feature2Title')}</h3>
                <p>{t('onboarding.feature2Description')}</p>
              </div>

              <div className="welcome-section">
                <span className="welcome-emoji">💰</span>
                <h3>{t('onboarding.feature3Title')}</h3>
                <p>{t('onboarding.feature3Description')}</p>
              </div>
            </div>

            <div className="welcome-trial-banner">
              <p className="trial-banner-text">✨ {t('onboarding.trialBanner')}</p>
            </div>

            <div className="onboarding-actions">
              <button type="button" className="btn btn-primary btn-large" onClick={handleNext}>
                {t('onboarding.getStarted')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="onboarding-step onboarding-plans">
            <h2>{t('onboarding.selectYourPlan')}</h2>
            <p className="plans-subtitle">{t('onboarding.selectPlanSubtitle')}</p>

            {loadingPlans ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>{t('onboarding.loadingPlans')}</p>
              </div>
            ) : (
              <PlanCardsGrid
                plans={plans}
                selectedPlanId={selectedPlan}
                onSelect={setSelectedPlan}
              />
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="onboarding-actions">
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                {t('onboarding.back')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={loading || !selectedPlan || plans.length === 0}
              >
                {loading ? t('onboarding.creating') : t('onboarding.confirmAndStart')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingWizard;
